import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FolderPlus, Star } from "@phosphor-icons/react";

export default function AdminShop() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selCat, setSelCat] = useState("");
  const [newCat, setNewCat] = useState({ name: "", parent_id: "" });
  const [form, setForm] = useState(null);
  const [savingCat, setSavingCat] = useState(false);
  const [savingProd, setSavingProd] = useState(false);

  const loadCats = () => api.get("/telecom/shop/admin/categories").then(({ data }) => setCategories(data.categories || []));
  const loadProds = () => api.get("/telecom/shop/admin/products").then(({ data }) => setProducts(data.products || []));
  useEffect(() => { loadCats().catch((e) => toast.error(apiError(e))); loadProds().catch(() => {}); }, []);

  const topCats = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const subsOf = (cid) => categories.filter((c) => c.parent_id === cid);
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const addCategory = async () => {
    if (!newCat.name.trim()) return;
    setSavingCat(true);
    try {
      await api.post("/telecom/shop/admin/categories", { name: newCat.name.trim(), parent_id: newCat.parent_id || null, sort_order: categories.length });
      setNewCat({ name: "", parent_id: "" }); await loadCats(); toast.success("Category added");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingCat(false); }
  };
  const renameCategory = async (c) => {
    const name = window.prompt("Category name", c.name); if (name == null) return;
    try { await api.patch(`/telecom/shop/admin/categories/${c.id}`, { name }); await loadCats(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleCat = async (c) => { try { await api.patch(`/telecom/shop/admin/categories/${c.id}`, { active: !c.active }); await loadCats(); } catch (e) { toast.error(apiError(e)); } };
  const delCategory = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This removes its sub-categories and products.`)) return;
    try { await api.delete(`/telecom/shop/admin/categories/${c.id}`); await loadCats(); await loadProds(); toast.success("Deleted"); } catch (e) { toast.error(apiError(e)); }
  };

  const blankProduct = (category_id) => ({ id: null, category_id, name: "", description: "", price_dollars: "", image: "", stock: "", active: true, featured: false });
  const editProduct = (p) => setForm({ ...p, price_dollars: (p.price_cents / 100).toFixed(2), stock: p.stock ?? "" });
  const saveProduct = async () => {
    if (!form.name.trim() || !form.category_id) { toast.error("Name and category required"); return; }
    const payload = {
      category_id: form.category_id, name: form.name.trim(), description: form.description || "",
      price_cents: Math.round(Number(form.price_dollars || 0) * 100), image: form.image || "",
      stock: form.stock === "" ? null : Number(form.stock), active: !!form.active, featured: !!form.featured,
    };
    setSavingProd(true);
    try {
      if (form.id) await api.patch(`/telecom/shop/admin/products/${form.id}`, payload);
      else await api.post("/telecom/shop/admin/products", payload);
      setForm(null); await loadProds(); toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingProd(false); }
  };
  const delProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/telecom/shop/admin/products/${p.id}`); await loadProds(); } catch (e) { toast.error(apiError(e)); }
  };

  const shownProducts = selCat ? products.filter((p) => { const ids = [selCat, ...subsOf(selCat).map((s) => s.id)]; return ids.includes(p.category_id); }) : products;

  return (
    <div className="mx-auto max-w-5xl space-y-8" data-testid="admin-shop">
      <div>
        <h1 className="font-display text-2xl font-semibold">Shop Manager</h1>
        <p className="mt-1 text-sm text-muted-foreground">Add categories, sub-categories and phone devices. Everything here appears on the public <span className="font-semibold">Shop Phone Devices</span> page.</p>
      </div>

      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 font-semibold">Categories</h2>
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">New category / sub-category</label>
            <input data-testid="new-cat-name" value={newCat.name} onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. VoIP Phones" className="mt-1 block w-56 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Parent (optional)</label>
            <select data-testid="new-cat-parent" value={newCat.parent_id} onChange={(e) => setNewCat((s) => ({ ...s, parent_id: e.target.value }))} className="mt-1 block w-48 rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">— Top level —</option>
              {topCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button onClick={addCategory} disabled={savingCat} data-testid="add-cat" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60"><FolderPlus size={16} weight="bold" /> Add</button>
        </div>
        <div className="space-y-2">
          {topCats.map((c) => (
            <div key={c.id}>
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className={`text-sm font-semibold ${c.active ? "" : "text-muted-foreground line-through"}`}>{c.name}</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelCat(c.id)} className="text-xs font-semibold text-primary">View products</button>
                  <button onClick={() => renameCategory(c)} title="Rename" className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                  <button onClick={() => toggleCat(c)} className="text-xs">{c.active ? "Hide" : "Show"}</button>
                  <button onClick={() => delCategory(c)} title="Delete" className="text-red-400 hover:text-red-500"><Trash size={16} /></button>
                </div>
              </div>
              {subsOf(c.id).length > 0 && (
                <div className="ml-5 mt-1 space-y-1 border-l border-border pl-3">
                  {subsOf(c.id).map((s) => (
                    <div key={s.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm">
                      <span className={s.active ? "" : "text-muted-foreground line-through"}>{s.name}</span>
                      <div className="flex items-center gap-3">
                        <button onClick={() => setSelCat(s.id)} className="text-xs font-semibold text-primary">Products</button>
                        <button onClick={() => renameCategory(s)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={15} /></button>
                        <button onClick={() => toggleCat(s)} className="text-xs">{s.active ? "Hide" : "Show"}</button>
                        <button onClick={() => delCategory(s)} className="text-red-400 hover:text-red-500"><Trash size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {topCats.length === 0 && <p className="text-sm text-muted-foreground">No categories yet.</p>}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-semibold">Products {selCat && <span className="text-sm font-normal text-muted-foreground">in {catName(selCat)}</span>}</h2>
          <div className="flex items-center gap-2">
            {selCat && <button onClick={() => setSelCat("")} className="text-xs text-muted-foreground">Show all</button>}
            <button onClick={() => setForm(blankProduct(selCat || (topCats[0]?.id || "")))} disabled={categories.length === 0} data-testid="add-product" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60"><Plus size={16} weight="bold" /> Add product</button>
          </div>
        </div>

        {form && (
          <div className="mb-4 space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-4" data-testid="product-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Category</label>
                <select value={form.category_id} onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.parent_id ? `${catName(c.parent_id)} › ${c.name}` : c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Name</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Price (USD)</label>
                <input type="number" step="0.01" value={form.price_dollars} onChange={(e) => setForm((f) => ({ ...f, price_dollars: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Stock (blank = unlimited)</label>
                <input type="number" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-muted-foreground">Image URL</label>
                <input value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} placeholder="https://…" className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-muted-foreground">Description</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Visible on storefront</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} /> Feature on storefront &amp; homepage (Best Sellers)</label>
            <div className="flex gap-2">
              <button onClick={saveProduct} disabled={savingProd} data-testid="save-product" className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60">{savingProd ? "Saving…" : "Save product"}</button>
              <button onClick={() => setForm(null)} className="rounded-full border border-border px-5 py-2 text-sm font-semibold">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {shownProducts.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-secondary">{p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" /> : null}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{p.name} {p.featured && <span className="text-xs font-semibold text-primary">★ Featured</span>} {!p.active && <span className="text-xs text-muted-foreground">(hidden)</span>}</p>
                <p className="text-xs text-muted-foreground">{catName(p.category_id)} · ${(p.price_cents / 100).toFixed(2)}{typeof p.stock === "number" ? ` · ${p.stock} in stock` : ""}</p>
              </div>
              <button onClick={async () => { try { await api.patch(`/telecom/shop/admin/products/${p.id}`, { featured: !p.featured }); await loadProds(); } catch (e) { toast.error(apiError(e)); } }} title="Toggle featured" data-testid={`feature-${p.id}`} className={p.featured ? "text-primary" : "text-muted-foreground hover:text-foreground"}><Star size={16} weight={p.featured ? "fill" : "regular"} /></button>
              <button onClick={() => editProduct(p)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
              <button onClick={() => delProduct(p)} className="text-red-400 hover:text-red-500"><Trash size={16} /></button>
            </div>
          ))}
          {shownProducts.length === 0 && <p className="text-sm text-muted-foreground">No products yet. Click “Add product”.</p>}
        </div>
      </section>
    </div>
  );
}
